import * as Yup from 'yup';
import { useState } from "react";
import { FormikProvider, getIn, useFormik } from "formik";
import { IRenter } from "../../../../../../../types/renter.type";
import { changePlanRenter } from "../../../../../../../common/services/renters.api";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import { AvailableAdditionsFieldFormik } from "../../../../forms/fields/AvailableAdditionsFieldFormik";
import { DateSubscriptionFormik } from "../../../../forms/fields/DateSubscriptionFormik";
import { AvailablePeriods } from "../../../../forms/fields/AvailablePeriods";
import { getPlanByLabel } from "../../../../../../../types/plan.type";
import TextAreaFormik from '../../../../../../../common/components/form/TextAreaFormik';
import { saveVehicleRecords } from '../../../../../../../common/services/vehicles.api';
import { AvailableVehiclesFieldFormik } from '../../../../forms/fields/AvailableVehiclesFieldFormik';
import VehicleBondingFlow from '../../../../../../../common/components/ui/registerVehilcePhotos/VehicleBondingFlow';
import ButtonWithLoader from '@/common/components/ui/loading/ButtonLoading';


const validationSchema = Yup.object().shape({
  reason: Yup.string().required("Motivo da troca é obrigatório"),
  returnReason: Yup.string().required("Descrição da cobrança é obrigatória"),
  subscriptionInitialDate: Yup.string()
    .required("Data de cobrança da primeira parcela é obrigatória"),
  period: Yup.object()
    .shape({
      value: Yup.string().required("Plano é obrigatório"),
      label: Yup.string().required("Plano é obrigatório"),
    })
    .required("Plano é obrigatório"),
});

export default function ChangePlanModal(renter: IRenter) {
  const { notifySuccess, notifyError } = useToastContext()
  const [bindChangePlan, setBindChangePlan] = useState(false)
  const [complement, setComplement] = useState('');
  const [plan, setPlan] = useState("");
  const vehicleHistory = renter?.desactivationData?.vehicleHistory || [];
  const actualVehicle = renter?.activationData?.vehicle;

  const vehicleId = actualVehicle?.id || '';
  const rawOdometer = renter?.activationData?.vehicle?.odometerVehicle;

  const currentOdometer =
    rawOdometer == null || rawOdometer === ''
      ? undefined // ou null, se preferir
      : Number(rawOdometer);

  // opcional: evita NaN se vier string inválida
  const safeCurrentOdometer =
    currentOdometer === undefined || Number.isNaN(currentOdometer)
      ? undefined
      : currentOdometer;



  const partnerId =
    actualVehicle?.partnerId ||
    (vehicleHistory.length > 0
      ? vehicleHistory[vehicleHistory.length - 1].partnerId
      : undefined);

  const handlePeriodChange = (selectedPeriod: string) => {
    setPlan(selectedPeriod);
    formik.setFieldValue("period", getPlanByLabel(selectedPeriod));
  };

  const initialRegisters = [
    { type: "Frente do veículo", content: "" },
    { type: "Lateral direita do veículo", content: "" },
    { type: "Lateral esquerda do veículo", content: "" },
    { type: "Traseira do veículo", content: "" },
    { type: "Odômetro", content: "" },
  ];

  const initialValues = {
    renterId: renter._id!,
    vehicleId,
    reason: "",
    returnReason: "",
    period: { value: "", label: "", months: 0 },
    additions: [],
    subscriptionInitialDate: "",
    registers: initialRegisters
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        if (!values.renterId) {
          notifyError("ID do locatário não encontrado");
          return;
        }
        setBindChangePlan(true);

        const result = await changePlanRenter(values);

        if (!result.ok) {
          notifyError(result.message as string);
          return;
        }
        notifySuccess('Plano trocado com sucesso');
      } catch (error) {
        notifyError((error as string) || "Erro ao trocar plano");
      } finally {
        setBindChangePlan(false);
      }
    },
  });

  const period = getIn(formik.values, 'period')
  const renterId = getIn(formik.values, 'renterId')
  const additions = getIn(formik.values, 'additions')
  const vehicle = getIn(formik.values, 'vehicle');

  const handleChangePlan = async (data: {
    renterId: string;
    vehicleId: string;
    photos: Array<{ type: string; content: string }>;
  }) => {
    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      formik.setTouched({
        reason: true,
        returnReason: true,
        subscriptionInitialDate: true,
        period: {
          value: true,
          label: true,
          months: true,
        },
      });
      return {
        ok: false,
        message: "Preencha os campos obrigatórios antes de continuar.",
      };
    }

    const result = await changePlanRenter({
      renterId: data.renterId,
      vehicleId: vehicle._id,
      reason: formik.values.reason,
      returnReason: formik.values.returnReason,
      period: formik.values.period,
      additions: formik.values.additions,
      subscriptionInitialDate: formik.values.subscriptionInitialDate,
      registers: data.photos,
    });

    return {
      ok: result.ok || false,
      message: result.message || (result.ok ? "Sucesso" : "Erro ao trocar plano"),
    };
  };

  const contractData = {
    renterId: renterId,
    vehicleId: formik.values.vehicleId,
    period: period,
    additions: additions,
  }

  // Verifica se deve mostrar o seletor de veículo e fotos
  const shouldShowVehicleSelector = !renter.activationData.vehicle.id && plan && partnerId;

  return (
    <div>
      <FormikProvider value={formik}>
        {/* Campos específicos do ChangePlan */}
        <section className="grid grid-cols-2 gap-2">
          <ComboBoxFormik
            name="reason"
            label="Motivo da troca"
            options={[
              { value: "", label: "-" },
              {
                value: "NovaMoto",
                label: "Experiência Insatisfatória com a Moto Atual",
              },
              {
                value: "MudanSeguro",
                label: "Mudança nas Condições de Seguro ",
              },
              {
                value: "AtualizaçãoContrato:",
                label: "Ofertas ou Promoções Especiais",
              },
              { value: "UpgradeVeículo:", label: "Upgrade de Veículo" },
              { value: "Econômico", label: "Econômico" },
              { value: "Acidente", label: "Acidente" },
              { value: "Outro", label: "Outro" },
            ]}
            required={true}
          />

          <DateSubscriptionFormik className="" />
          <AvailablePeriods onChange={handlePeriodChange} action="changePlan" odometerLocavibe={safeCurrentOdometer} />
          <AvailableAdditionsFieldFormik />
        </section>

        {/* Campos adicionais específicos */}
        <div className="flex flex-col gap-6 mt-4">
          <div className="flex-1">
            <TextAreaFormik
              label="Complemento sobre o motivo da troca:"
              name="returnReason"
              value={complement}
              onChange={(e) => setComplement(e.target.value)}
              required={true}
            />
          </div>
        </div>

        {/* Componente genérico - só aparece se necessário */}
        {shouldShowVehicleSelector && (
          <VehicleBondingFlow
            renterId={renter._id!}
            renterName={renter.name}
            onSavePhotos={saveVehicleRecords}
            onBond={handleChangePlan}
            showVehicleSelector={true}
            contractData={contractData}
            buttonLabel="Trocar Plano"
            showPhotosDeliveryWarning={true}
            vehicleSelectorComponent={
              <AvailableVehiclesFieldFormik
                listAll={false}
                companyId={renter.companyId}
                required={true}
                renterId={renterId}
                plan={formik.values.period?.label || ''}
              />
            }
          />
        )}

        {/* Botão para casos onde não precisa do VehicleBondingFlow */}
        {!shouldShowVehicleSelector && (
          <section>
            <div className="flex justify-end mt-6">
              <ButtonWithLoader
                type="button"
                disabled={!formik.isValid}
                isLoading={bindChangePlan}
                onClick={formik.handleSubmit}
              >
                Trocar Plano
              </ButtonWithLoader>
            </div>
          </section>
        )}
      </FormikProvider>
    </div>
  );
}
